import { useAuth } from '@lastbrand/fe-common/universal-auth'
import { useSearchParams } from 'next/navigation'
import { useTranslation } from 'next-i18next'
import { SyntheticEvent, useCallback, useEffect, useState } from 'react'

import { useQuinceRouter as useRouter } from '@src/modules/navigation'
import { FEATURES, MODULES } from '@src/quince-site-common/src/constants/template-info'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import { getSanitizedHtml } from '@src/quince-site-common/src/utils/getSanitizedHtml'
import isBrowser from '@src/quince-site-common/src/utils/isBrowser'
import { captureException } from '@src/quince-site-common/src/utils/sentryUtil'
import { PasswordValidation } from '@src/quince-site-integrated/src/components/Account/password-validation'

import FormFooter from '../../../../components/Auth/FormFooter'
import FormHeader from '../../../../components/Auth/FormHeader'
import { BoxStyledErrorMessage } from '../../../../components/FormElements'
import { useRedirectToCheckoutIfApplicable } from '../../../Auth/hooks/use-login-redirect'
import { ContinueButton } from '../../../LogIn/Components/ContinueButton'
import { Email } from '../../../LogIn/Components/Email'
import { FirstAndLastName } from '../../../LogIn/Components/FirstAndLastName'
import styles from '../../../LogIn/Components/loginComponents.module.scss'
import { Password } from '../../../LogIn/Components/Password'
import { TnC } from '../../../LogIn/Components/TnC'
import useForm from '../../../LogIn/hook/useForm'

interface SignUpVariantAPropTypes {
  serverError?: { message: string }
  email: string
  errors?: Record<string, { message: string }>
  loading?: boolean
  passwordError?: boolean
  signUp: (name?: string | null, username?: string | null, password?: string | null, isPasswordValid?: boolean) => void
}

// @i18n-ignore
const SIGN_UP_FORM_A = 'sign-up-form-A'

const SignUpVariantA = ({
  serverError,
  email,
  errors = {},
  loading,
  passwordError,
  signUp,
}: SignUpVariantAPropTypes) => {
  const { t } = useTranslation('common')
  const [isPasswordFocused, setIsPasswordFocused] = useState(false)
  const [isValid, setIsValid] = useState(false)
  const router = useRouter()
  const deviceInfo = useDeviceInfo()
  const searchParams = useSearchParams()
  const redirectToCheckoutIfApplicable = useRedirectToCheckoutIfApplicable()
  const isDesktop = deviceInfo.deviceType === DEVICE_TYPES.DESKTOP
  const userMailQuery = searchParams.get('userMail')
  const emailInUrl = (() => {
    if (!userMailQuery) return ''
    try {
      return atob(userMailQuery)
    } catch {
      let userAgent = ''
      if (isBrowser()) {
        userAgent = window.navigator?.userAgent
      }
      captureException(new Error('Failed to decode userMailQuery'), {
        feature: FEATURES.SIGN_UP,
        module: MODULES.SIGN_UP,
        extraInfo: {
          // @i18n-ignore
          userMail: userMailQuery,
          userAgent,
        },
      })
      return ''
    }
  })()
  const { isLoggedIn, isInitializing } = useAuth()

  const {
    email: userEmail,
    password,
    firstAndLastName,
    onPasswordChange,
    onFirstAndLastNameChange,
    onEmailChange,
  } = useForm({ email: emailInUrl })

  const onFormSubmit = (e: SyntheticEvent) => {
    e.preventDefault()
    signUp(firstAndLastName, userEmail, password, isValid)
  }

  const onBackClick = useCallback(() => {
    router.push('/log-in')
  }, [router])

  useEffect(() => {
    if (!isInitializing && isLoggedIn) {
      redirectToCheckoutIfApplicable()
    }
  }, [redirectToCheckoutIfApplicable, isLoggedIn, isInitializing])

  const showValidator = isPasswordFocused || (passwordError && Boolean(password?.length))

  return (
    <form onSubmit={onFormSubmit} id="sign-up-form-A" noValidate>
      <FormHeader>
        <div className={styles.formHeading}>{isDesktop ? t('register') : t('create_your_account')}</div>
        <div className={styles.formSubHeading}>{t('free_shipping_365_day_ret_atbvw9')}</div>
      </FormHeader>
      {serverError && (
        <BoxStyledErrorMessage
          dangerouslySetInnerHTML={{
            __html: getSanitizedHtml(typeof serverError === 'string' ? serverError : serverError.message),
          }}
        />
      )}
      <Email
        errors={errors}
        value={userEmail}
        disabled={!!emailInUrl}
        showEditIcon={!!emailInUrl}
        onEdit={onBackClick}
        onChange={onEmailChange}
      />
      <FirstAndLastName
        errors={errors}
        disabled={loading}
        value={firstAndLastName}
        onChange={onFirstAndLastNameChange}
      />
      <Password
        errors={showValidator ? {} : errors}
        email={email}
        disabled={loading}
        showForgotPassword={false}
        value={password}
        onChange={onPasswordChange}
        onFocus={() => {
          setIsPasswordFocused(true)
        }}
      />
      {showValidator && (
        <PasswordValidation
          value={password ?? ''}
          showError={Boolean(errors.password ?? passwordError)}
          setValidPassword={setIsValid}
          className={styles.validator}
        />
      )}
      <TnC />
      <FormFooter>
        <ContinueButton loading={loading} form={SIGN_UP_FORM_A} />
      </FormFooter>
    </form>
  )
}

export { SignUpVariantA }
